import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';

import InvoiceCategoryTabs from '../../../components/Billing/Invoice/InvoiceCategoryTabs';
import SearchBar from '../../../components/Billing/Invoice/InvoiceSearchBar';
import InvoiceFilter, { InvoiceFilterValues } from '../../../components/Billing/Invoice/InvoiceFilter';
import { ReactComponent as FunnelIcon } from '../../../assets/SVG/Filter Icon Box.svg';
import CheckedInList from './CheckedIn_Advance/CheckedInList';
import ReceivedList from './Recieved_Advance/ReceivedList';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppQuery } from '../../../services/appQuery';
import { AdvancesStats, getAdvancesStatsURL } from './advance.helper';
import { getRequest } from '../../../services/httpService';
import { usePatientContext } from '../../../components/SubPatinetProvider';
import HeaderProfile from '../../../components/header_Profile/header_Profile.index';

type AdvanceTabKey = 'checked-in' | 'advance-logs';

const PatientAdvance: React.FC = () => {
  // Header states
  const [activeTab, setActiveTab] = useState<AdvanceTabKey>('checked-in');
  const [search, setSearch] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState<InvoiceFilterValues>({ fromDate: '', toDate: '', billType: '' });
  const location = useLocation();
  const navigate = useNavigate();
  const { patientId } = usePatientContext();


  const { data: AdvancesStats } = useAppQuery<AdvancesStats>(
    ['AdvancesStats', patientId],
    () => {
      const baseUrl = getAdvancesStatsURL();
      const url = patientId ? `${baseUrl}?patient_id=${patientId}` : baseUrl;
      return getRequest<AdvancesStats>(url);
    },
    { staleTime: 0, gcTime: 0, enabled: true, refetchOnMount: true }
  );
    // Category tabs with counts (wire to data later)
  const { t: tBilling } = useTranslation('billing_config');
  const categories = useMemo(() => ([
    { name: 'checked-in', label: tBilling('Advance.checked_in', 'Checked In'), count: AdvancesStats?.checked_in_today },
    { name: 'advance-logs', label: tBilling('Advance.advance_log', 'Advance Log'), count: AdvancesStats?.total_credit_history },
  ]), [AdvancesStats, tBilling]);
  
  // Sync active tab with URL query parameters
  useEffect(() => { 
    const params = new URLSearchParams(location.search);
    if (params.has('checked-in')) {
      setActiveTab('checked-in');
    } else if (params.has('Logs') || params.has('logs') || params.has('received')) {
      setActiveTab('advance-logs');
    }
  }, [location.search]);



  const renderTabContent = () => {
    switch (activeTab) {
    case 'checked-in':
      return <CheckedInList patientId={patientId} search={search} />;
    case 'advance-logs':
      return <ReceivedList patientId={patientId} search={search} filters={filters} />;
    default:
      return null;
    }
  };

  return (
    <>
      <HeaderProfile />
      <Box sx={{ p: 3, pt: 2 }} data-testid="patient-advance-container">
        {/* Header: Tabs left, filter + search right */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 2 }} data-testid="patient-advance-header-section">
          <InvoiceCategoryTabs
            activeCategory={activeTab}
            categories={categories}
            onCategoryChange={(cat) => {
              setActiveTab(cat.name as AdvanceTabKey);
              if (location.search) {
                navigate(location.pathname, { replace: true });
              }
            }}
          />

          <Stack direction="row" spacing={1.5} alignItems="center">
            {activeTab !== 'checked-in' && (
              <Tooltip title={tBilling('Advance.filters', { defaultValue: 'Filters' })} arrow>
                <IconButton aria-label={tBilling('Advance.aria.open_filters', { defaultValue: 'Open filters' })} onClick={() => setIsFilterOpen(true)} sx={{ width: 40, height: 40, p: 0 }}>
                  <FunnelIcon style={{ width: 40, height: 40 }} />
                </IconButton>
              </Tooltip>
            )}
            {!patientId && (
              <SearchBar
                height={40}
                value={search}
                onChange={setSearch}
                placeholder={tBilling('Advance.search_by_patient_name_mrn_or_mobile_no')}
                minWidth={420} />
            )}
          </Stack>
        </Stack>

        <Divider sx={{ mb: 2 }} />

        {/* Content area for the active tab */}
        {renderTabContent()}

        {/* Shared filter modal */}
        <InvoiceFilter
          open={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          onApply={(vals) => { setFilters(vals); setIsFilterOpen(false); } }
          showBillType={false}
          initialValues={filters} />
      </Box></>
  );
};

export default PatientAdvance;
